// Timing the gestures, from inside the app.
//
// ── WHY IN-APP AND NOT FROM A TEST HARNESS ──────────────────────────────────────
// Every attempt to time a drag from outside produced numbers that were about the harness rather than
// the app: a hidden browser pane throttles `requestAnimationFrame` and coarsens timers, and the 456ms
// that came back from one such run was a measurement artefact reported as if it were a fact. Marks
// placed where the work actually happens cannot be wrong about that — they are recorded by the same
// code path a planner triggers, in whatever tab they are looking at.
//
// ── WHAT IS MEASURED ────────────────────────────────────────────────────────────
//   down→preview      the gesture "takes": is there a visible thing under the finger yet
//   move→paint        one pointermove to the position being updated
//   drop→optimistic   the chip is in its new cell, before the server has said anything
//   drop→server       the write is confirmed
//
// The first three are what "direct aanvoelen" means. The fourth is allowed to be slow: by then the
// planner has already seen the answer.
const ON = typeof performance !== 'undefined' && typeof performance.mark === 'function';

export function mark(name: string): void {
  if (ON) { try { performance.mark(name); } catch { /* a mark must never break a gesture */ } }
}

export function measure(name: string, from: string, to: string): void {
  if (!ON) return;
  try { performance.measure(name, from, to); } catch { /* a missing start mark is not an error */ }
}

/** Every drag measure recorded so far, as milliseconds. Read from the console or a test. */
export function dragTimings(): Record<string, number[]> {
  if (!ON) return {};
  const out: Record<string, number[]> = {};
  for (const m of performance.getEntriesByType('measure')) {
    if (!m.name.startsWith('drag:')) continue;
    (out[m.name] ??= []).push(Math.round(m.duration * 10) / 10);
  }
  return out;
}

// Exposed deliberately: this is how the numbers in the sprint report were taken, and how anybody can
// take them again without a build change.
if (typeof window !== 'undefined') {
  (window as unknown as { bruceDragTimings: typeof dragTimings }).bruceDragTimings = dragTimings;
}
